import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Row,
  Modal,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Pagination,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory,
  faEye,
  faCalendarAlt,
  faUser,
  faFileAlt,
  faMoneyBillWave,
  faSearch,
  faFilter,
  faSync,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faTimesCircle,
  faPlay,
  faUserCheck,
  faGavel,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { getSector, fetchApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import CustomTypeahead from "../components/CustomTypeahead";
import CustomTable from "../../components/CustomTable";
import moment from "moment";
import { Routes } from "../../routes";
import { useNavigate } from "react-router-dom";

const getStatusInfo = (status) => {
  const statusLower = status?.toLowerCase() || "";
  const statusMap = {
    'created': {
      title: 'PV Created',
      description: 'Payment Voucher has been created and is ready for processing',
      icon: faPlay,
      type: 'info'
    },
    'pending': {
      title: 'Pending Review',
      description: 'Awaiting review and verification by the next approver',
      icon: faClock,
      type: 'warning'
    },
    'checked': {
      title: 'Checked & Verified',
      description: 'Payment details have been checked and verified for accuracy',
      icon: faUserCheck,
      type: 'info'
    },
    'reviewed': {
      title: 'Under Review',
      description: 'Payment Voucher is being reviewed by authorized personnel',
      icon: faEye,
      type: 'info'
    },
    'approved': {
      title: 'Approved',
      description: 'Payment has been approved and is ready for final processing',
      icon: faCheckCircle,
      type: 'success'
    },
    'ag approved': {
      title: 'Auditor General Approved',
      description: 'Final approval received from the Auditor General office',
      icon: faGavel,
      type: 'success'
    },
    'completed': {
      title: 'Payment Completed',
      description: 'Payment has been successfully processed and completed',
      icon: faHandshake,
      type: 'success'
    },
    'returned': {
      title: 'Returned for Correction',
      description: 'Payment Voucher has been returned for corrections or additional information',
      icon: faExclamationTriangle,
      type: 'warning'
    },
    'rejected': {
      title: 'Rejected',
      description: 'Payment Voucher has been rejected and will not be processed',
      icon: faTimesCircle,
      type: 'danger'
    }
  };
  if (statusMap[statusLower]) return statusMap[statusLower];
  for (const [key, value] of Object.entries(statusMap)) {
    if (statusLower.includes(key)) return value;
  }
  return {
    title: status || 'Unknown Status',
    description: 'Status information is being processed',
    icon: faFileAlt,
    type: 'secondary'
  };
};

const getProgressSteps = (statusHistory) => {
  const commonSteps = [
    { key: 'created', label: 'Created', icon: faPlay },
    { key: 'pending', label: 'Pending', icon: faClock },
    { key: 'checked', label: 'Checked', icon: faUserCheck },
    { key: 'reviewed', label: 'Reviewed', icon: faEye },
    { key: 'approved', label: 'Approved', icon: faCheckCircle },
    { key: 'ag approved', label: 'AG Approved', icon: faGavel },
    { key: 'completed', label: 'Completed', icon: faHandshake }
  ];
  const historyStatuses = statusHistory.map(h => h.pv_status.toLowerCase());
  const currentStatus = historyStatuses[historyStatuses.length - 1];
  return commonSteps.map(step => {
    if (historyStatuses.includes(step.key)) {
      return { ...step, status: step.key === currentStatus ? 'current' : 'completed' };
    } else if (step.key === 'created') {
      return { ...step, status: 'completed' };
    } else {
      return { ...step, status: 'pending' };
    }
  });
};

function AllPvReportTracking({ mdaType } = {}) {
  const history = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPv, setSelectedPv] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [mdaFilter, setMdaFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedMda, setAppliedMda] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1, limit: 50, total: 0, totalPages: 0
  });

  const [mdaOptions, setMdaOptions] = useState([]);
  useEffect(() => {
    getSector({ type: "mdas" }, "mdas", (data) => {
      setMdaOptions(
        (data || []).map((i) => ({ mda_name: i.description, mda_code: i.head }))
      );
    });
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const paramObj = { page, limit: 50 };
    if (appliedSearch.trim()) paramObj.search = appliedSearch.trim();
    if (appliedStatus) paramObj.status = appliedStatus;
    if (appliedMda.trim()) paramObj.mda_name = appliedMda.trim();
    if (appliedDateFrom) paramObj.date_from = appliedDateFrom;
    if (appliedDateTo) paramObj.date_to = appliedDateTo;
    if (mdaType) paramObj.mda_type = mdaType;

    const params = new URLSearchParams(paramObj);
    fetchApi(`get_all_pv_history?${params.toString()}`)
      .then((data) => {
        if (data.success) {
          setList(data.result || []);
          setPagination(data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
        }
      })
      .catch((err) => console.error("Error fetching PV history:", err))
      .finally(() => setLoading(false));
  }, [page, appliedSearch, appliedStatus, appliedMda, appliedDateFrom, appliedDateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApplyFilters = () => {
    setAppliedSearch(search);
    setAppliedStatus(statusFilter);
    setAppliedMda(mdaFilter);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setMdaFilter("");
    setDateFrom("");
    setDateTo("");
    setAppliedSearch("");
    setAppliedStatus("");
    setAppliedMda("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setPage(1);
  };

  const handleViewHistory = (pv) => {
    setSelectedPv(pv);
    setShowHistoryModal(true);
  };

  const getStatusBadgeVariant = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("approved") || statusLower.includes("completed")) return "success";
    if (statusLower.includes("pending") || statusLower.includes("review")) return "warning";
    if (statusLower.includes("returned") || statusLower.includes("rejected")) return "danger";
    if (statusLower.includes("checked") || statusLower.includes("received")) return "info";
    return "primary";
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    if (startPage > 1) {
      items.push(<Pagination.Item key={1} onClick={() => setPage(1)}>1</Pagination.Item>);
      if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }
    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
          {p}
        </Pagination.Item>
      );
    }
    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      items.push(
        <Pagination.Item key={pagination.totalPages} onClick={() => setPage(pagination.totalPages)}>
          {pagination.totalPages}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-3 px-3">
        <div className="text-muted small">
          Showing {((page - 1) * pagination.limit) + 1}–{Math.min(page * pagination.limit, pagination.total)} of{" "}
          {pagination.total} entries
        </div>
        <Pagination className="mb-0">
          <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
          {items}
          <Pagination.Next disabled={page === pagination.totalPages} onClick={() => setPage(p => p + 1)} />
        </Pagination>
      </div>
    );
  };

  const fields = [
    { title: "PV Code", value: "pv_code" },
    { title: "Approval No.", value: "approval_no" },
    { title: "MDA Name", value: "mda_name" },
    { title: "MDA Code", value: "mda_code" },
    { title: "Contractor", value: "contractor_name" },
    {
      title: "Amount (N)",
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.amount)}</div>
      ),
    },
    {
      title: "Current Status",
      custom: true,
      component: (item) => (
        <div color={getStatusBadgeVariant(item.current_status)}>
          {item.current_status}
        </div>
      ),
    },
    {
      title: "PV Date",
      custom: true,
      component: (item) => (
        <div>{moment(item.pv_date).format("DD/MM/YYYY")}</div>
      ),
    },
    {
      title: "Actions",
      custom: true,
      component: (item) => (
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-primary" onClick={() => handleViewHistory(item)}>
            <FontAwesomeIcon icon={faHistory} className="me-1" />
            History
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => {
            history(
              `${Routes.pv.pvReportView}?${item.approval_no ? `approval_no=${encodeURIComponent(item.approval_no)}&` : ''}approval_type=${encodeURIComponent(item.project_type)}&pv_code=${encodeURIComponent(item.pv_code)}`
            );
          }}>
            <FontAwesomeIcon icon={faEye} className="me-1" />
            View PV
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            All PV Report Tracking
          </h4>
          <p className="mb-0">
            Track all Payment Vouchers across all MDAs - Total: {pagination.total} PVs
          </p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <ButtonGroup>
            <Button variant="outline-primary" size="sm" onClick={fetchData}>
              <FontAwesomeIcon icon={faSync} className="me-1" />
              Refresh
            </Button>
            <Button variant="outline-primary" size="sm">
              Export
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search PVs</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="PV Code, Approval No, Contractor, Narration..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  />
                  <Button variant="outline-primary" onClick={handleApplyFilters} title="Search">
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Search by using PV Code, Approval No, Contractor, Narration
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="Created">Created</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Checked">Checked</option>
                  <option value="Reviewed">Reviewed</option>
                  <option value="AG Approved">AG Approved</option>
                  <option value="Returned">Returned</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>MDA Name</Form.Label>
                <CustomTypeahead
                  labelKey="mda_name"
                  options={mdaOptions}
                  selected={mdaFilter ? [{ mda_name: mdaFilter }] : []}
                  onChange={(s) => setMdaFilter(s.length ? s[0].mda_name : "")}
                  placeholder="Search MDA name..."
                />
                <Form.Text className="text-muted">
                  Leave empty to search across all MDAs
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date From</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date To</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={12} className="d-flex gap-2 justify-content-center">
              <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Apply
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="p-0" style={{ height: "70vh", overflow: "scroll" }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : list.length > 0 ? (
            <CustomTable fields={fields} data={list} />
          ) : (
            <div className="text-center p-5">
              <FontAwesomeIcon icon={faFileAlt} size="3x" className="text-muted mb-3" />
              <h5 className="text-muted">No PVs Found</h5>
              <p className="text-muted">
                {appliedSearch || appliedStatus || appliedMda || appliedDateFrom || appliedDateTo
                  ? "No PVs match your current filters. Try adjusting your search criteria."
                  : "No Payment Vouchers are available in the system."}
              </p>
            </div>
          )}
        </Card.Body>
        {renderPagination()}
      </Card>

      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="xl">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center">
            <div className="me-3">
              <FontAwesomeIcon icon={faHistory} className="me-2 text-primary" />
              Payment Voucher Journey
            </div>
            <Badge variant="outline-primary" className="fs-6">
              {selectedPv?.pv_code}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPv && (
            <>
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Header className="bg-gradient-primary text-white">
                  <h6 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    Payment Voucher Summary
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-4">
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">PV Code</div>
                        <div className="info-value text-primary fw-bold">{selectedPv.pv_code}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Approval Number</div>
                        <div className="info-value">{selectedPv.approval_no}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Current Status</div>
                        <Badge color={getStatusBadgeVariant(selectedPv.current_status)} className="status-badge">
                          {selectedPv.current_status}
                        </Badge>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">MDA</div>
                        <div className="info-value">{selectedPv.mda_name}</div>
                        {selectedPv.mda_code && (
                          <div className="info-subtext">Code: {selectedPv.mda_code}</div>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Contractor</div>
                        <div className="info-value">{selectedPv.contractor_name}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Amount</div>
                        <div className="info-value text-success fw-bold">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                          ₦{formatNumber(selectedPv.amount)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {selectedPv.status_history && selectedPv.status_history.length > 0 && (
                <Card className="mb-4 border-0 shadow-sm">
                  <Card.Body className="p-4">
                    <h6 className="mb-3 fw-bold text-dark">
                      <FontAwesomeIcon icon={faClock} className="me-2 text-info" />
                      Processing Progress
                    </h6>
                    <div className="progress-steps">
                      {getProgressSteps(selectedPv.status_history).map((step, index) => (
                        <div key={index} className={`progress-step ${step.status}`}>
                          <div className="step-icon">
                            <FontAwesomeIcon icon={step.icon} />
                          </div>
                          <div className="step-label">{step.label}</div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              <h6 className="mb-4">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                Payment Voucher Journey
              </h6>

              {selectedPv.status_history && selectedPv.status_history.length > 0 ? (
                <div className="enhanced-timeline">
                  {selectedPv.status_history.map((history, index) => {
                    const isLatest = index === selectedPv.status_history.length - 1;
                    const statusInfo = getStatusInfo(history.pv_status);
                    const prevHistory = index > 0 ? selectedPv.status_history[index - 1] : null;
                    let durationStr = null;
                    if (prevHistory) {
                      const diffMs = moment(history.updated_at).diff(moment(prevHistory.updated_at));
                      const duration = moment.duration(diffMs);
                      const days = Math.floor(duration.asDays());
                      const hours = duration.hours();
                      const minutes = duration.minutes();
                      const seconds = duration.seconds();
                      const parts = [];
                      if (days > 0) parts.push(`${days}d`);
                      if (hours > 0) parts.push(`${hours}h`);
                      if (minutes > 0) parts.push(`${minutes}m`);
                      if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
                      durationStr = parts.join(" ");
                    }
                    return (
                      <div key={index} className={`timeline-item ${isLatest ? 'current' : 'completed'}`}>
                        <div className="timeline-marker">
                          <div className={`timeline-icon ${statusInfo.type}`}>
                            <FontAwesomeIcon icon={statusInfo.icon} />
                          </div>
                        </div>
                        <div className="timeline-content">
                          <Card className={`timeline-card ${statusInfo.type}`}>
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="mb-1 fw-bold text-dark">{statusInfo.title}</h6>
                                  <p className="mb-1 text-muted small">{statusInfo.description}</p>
                                  {durationStr && (
                                    <div className="text-muted small mt-1 d-flex align-items-center">
                                      <FontAwesomeIcon icon={faClock} className="me-1 text-info" style={{ fontSize: "10px" }} />
                                      <span>Time spent: <strong className="text-dark">{durationStr}</strong></span>
                                    </div>
                                  )}
                                </div>
                                <Badge variant={getStatusBadgeVariant(history.pv_status)} className="status-badge">
                                  {history.pv_status}
                                </Badge>
                              </div>
                              <div className="timeline-meta">
                                <Row className="g-2">
                                  <Col md={6}>
                                    <div className="d-flex align-items-center text-muted small">
                                      <FontAwesomeIcon icon={faUser} className="me-2" />
                                      <div>
                                        <div className="fw-semibold">{history.updated_by_name || 'System'}</div>
                                        {history.user_mda && <div className="text-muted">{history.user_mda}</div>}
                                      </div>
                                    </div>
                                  </Col>
                                  <Col md={6} className="text-end">
                                    <div className="text-muted small">
                                      <div className="fw-semibold">{moment(history.updated_at).format("DD MMM YYYY")}</div>
                                      <div>{moment(history.updated_at).format("HH:mm")} • {moment(history.updated_at).fromNow()}</div>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                              {history.remarks && (
                                <div className="timeline-remarks mt-3 p-2 bg-light rounded">
                                  <div className="d-flex align-items-start">
                                    <FontAwesomeIcon icon={faFileAlt} className="me-2 mt-1 text-muted" />
                                    <div>
                                      <strong className="small text-dark">Remarks:</strong>
                                      <div className="text-muted small mt-1">{history.remarks}</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center p-5">
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faHistory} size="3x" className="text-muted mb-3" />
                    <h6 className="text-muted mb-2">No Journey History</h6>
                    <p className="text-muted small">
                      This Payment Voucher doesn't have any recorded status changes yet.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="text-muted small">
              <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
              Last updated: {selectedPv && moment(selectedPv.status_history?.[selectedPv.status_history.length - 1]?.updated_at).format("DD MMM YYYY, HH:mm")}
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowHistoryModal(false)} className="me-2">
                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                Close
              </Button>
              {selectedPv && (
                <Button variant="primary" onClick={() => {
                  setShowHistoryModal(false);
                  history(
                    `${Routes.pv.pvReportView}?${selectedPv.approval_no ? `approval_no=${encodeURIComponent(selectedPv.approval_no)}&` : ''}approval_type=${encodeURIComponent(selectedPv.project_type)}&pv_code=${encodeURIComponent(selectedPv.pv_code)}`
                  );
                }}>
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  View Full PV
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <style>{`
        .enhanced-timeline { position: relative; padding-left: 0; }
        .bg-gradient-primary { background: linear-gradient(135deg, #007bff, #0056b3) !important; }
        .info-item { text-align: left; }
        .info-label { font-size: 0.875rem; color: #6c757d; font-weight: 500; margin-bottom: 0.25rem; text-transform: uppercase; letter-spacing: 0.5px; }
        .info-value { font-size: 1rem; color: #212529; font-weight: 600; margin-bottom: 0.125rem; }
        .info-subtext { font-size: 0.75rem; color: #6c757d; font-style: italic; }
        .progress-steps { display: flex; justify-content: space-between; align-items: center; position: relative; margin: 1rem 0; }
        .progress-steps::before { content: ''; position: absolute; top: 20px; left: 0; right: 0; height: 2px; background: #e9ecef; z-index: 1; }
        .progress-step { display: flex; flex-direction: column; align-items: center; position: relative; z-index: 2; flex: 1; }
        .step-icon { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; margin-bottom: 0.5rem; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .progress-step.completed .step-icon { background: #28a745; color: white; }
        .progress-step.current .step-icon { background: #007bff; color: white; animation: pulse 2s infinite; }
        .progress-step.pending .step-icon { background: #f8f9fa; color: #6c757d; border-color: #dee2e6; }
        .step-label { font-size: 0.75rem; font-weight: 600; text-align: center; color: #6c757d; max-width: 80px; line-height: 1.2; }
        .progress-step.completed .step-label { color: #28a745; }
        .progress-step.current .step-label { color: #007bff; }
        .timeline-item { position: relative; display: flex; margin-bottom: 2rem; min-height: 120px; }
        .timeline-item:last-child { margin-bottom: 0; }
        .timeline-marker { position: relative; flex-shrink: 0; width: 60px; display: flex; justify-content: center; align-items: flex-start; padding-top: 1rem; }
        .timeline-item:not(:last-child) .timeline-marker::after { content: ''; position: absolute; top: 50px; left: 50%; transform: translateX(-50%); width: 2px; height: calc(100% + 2rem); background: linear-gradient(to bottom, #007bff, #e9ecef); z-index: 1; }
        .timeline-icon { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; position: relative; z-index: 2; box-shadow: 0 2px 8px rgba(0,0,0,0.15); border: 3px solid white; }
        .timeline-icon.success { background: linear-gradient(135deg, #28a745, #20c997); }
        .timeline-icon.info { background: linear-gradient(135deg, #17a2b8, #007bff); }
        .timeline-icon.warning { background: linear-gradient(135deg, #ffc107, #fd7e14); }
        .timeline-icon.danger { background: linear-gradient(135deg, #dc3545, #e83e8c); }
        .timeline-icon.secondary { background: linear-gradient(135deg, #6c757d, #495057); }
        .timeline-content { flex: 1; margin-left: 1rem; }
        .timeline-card { border: none; box-shadow: 0 2px 12px rgba(0,0,0,0.08); transition: all 0.3s ease; border-radius: 12px; overflow: hidden; }
        .timeline-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.12); transform: translateY(-2px); }
        .timeline-card.success { border-left: 4px solid #28a745; }
        .timeline-card.info { border-left: 4px solid #17a2b8; }
        .timeline-card.warning { border-left: 4px solid #ffc107; }
        .timeline-card.danger { border-left: 4px solid #dc3545; }
        .timeline-card.secondary { border-left: 4px solid #6c757d; }
        .timeline-item.current .timeline-card { background: linear-gradient(135deg, #f8f9fa, #ffffff); border-left-width: 6px; }
        .timeline-item.current .timeline-icon { animation: pulse 2s infinite; }
        @keyframes pulse { 0% { box-shadow: 0 2px 8px rgba(0,0,0,0.15), 0 0 0 0 rgba(0, 123, 255, 0.7); } 70% { box-shadow: 0 2px 8px rgba(0,0,0,0.15), 0 0 0 10px rgba(0, 123, 255, 0); } 100% { box-shadow: 0 2px 8px rgba(0,0,0,0.15), 0 0 0 0 rgba(0, 123, 255, 0); } }
        .timeline-meta { border-top: 1px solid #f1f3f4; padding-top: 0.75rem; margin-top: 0.75rem; }
        .timeline-remarks { border: 1px solid #e9ecef; background: #f8f9fa; }
        .status-badge { font-size: 0.75rem; padding: 0.375rem 0.75rem; border-radius: 20px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .empty-state { padding: 2rem; border: 2px dashed #dee2e6; border-radius: 12px; background: #f8f9fa; }
        @media (max-width: 768px) {
          .timeline-marker { width: 50px; }
          .timeline-icon { width: 35px; height: 35px; font-size: 14px; }
          .timeline-content { margin-left: 0.75rem; }
          .timeline-meta .row { flex-direction: column; }
          .timeline-meta .col-md-6:last-child { text-align: left !important; margin-top: 0.5rem; }
          .progress-steps { flex-wrap: wrap; gap: 1rem; }
          .progress-step { flex: 0 0 calc(33.333% - 1rem); margin-bottom: 1rem; }
          .progress-steps::before { display: none; }
          .step-icon { width: 35px; height: 35px; font-size: 14px; }
          .step-label { font-size: 0.7rem; max-width: 70px; }
        }
      `}</style>
    </>
  );
}

export default AllPvReportTracking;
